/**
 * App-level configuration knobs. Safe to change freely — none of these are
 * secrets. The actual charged price and any intro discount live in Stripe
 * (Price + coupon), so the pricing decision does not require a code change:
 * these are display strings + the free-trial length only.
 */

/**
 * Length of the in-app free trial (no credit card required), counted from the
 * user's signup date. When it lapses, the paywall appears. Change anytime.
 */
export const TRIAL_DAYS = 14;

/**
 * Read-only grace period after a paid subscription lapses (payment fails or
 * cancels), before full lockout — backlog LAUNCH-9. Counted from
 * `subscriptions.access_locked_at`. Proposed default, not final — LAUNCH-7
 * (attorney ToS/Privacy review) should confirm or override this before it's
 * presented as firm policy anywhere customer-facing. Deliberately does NOT
 * apply to a first-time trial expiring with no prior subscription.
 */
export const GRACE_DAYS = 7;


/** Display-only copy for the paywall. Real price comes from Stripe. */
export const PLAN_NAME = "SnapDraw — Full Access";
export const PLAN_BLURB = "Unlimited quotes and G702/G703 payment applications — up to 3 user accounts.";
/** Working assumption only; final price is set on the Stripe Price and shown by Checkout. */
export const PLAN_PRICE_HINT = "$9.99 / mo";

/**
 * Company + legal links, shown in the footer. COMPANY_NAME is the parent brand
 * used across all Shaman apps (SnapDraw, Siteline, …). The legal URLs default to
 * the in-app /terms and /privacy routes; point them at full https URLs if you
 * later host the reviewed docs elsewhere (the footer handles external links).
 */
export const COMPANY_NAME = "Shaman Technologies";
export const TERMS_URL = "/terms";
export const PRIVACY_URL = "/privacy";

/**
 * Production domain, shown as plain text in the mobile paywall (backlog
 * MOBILE-1, hybrid billing model). Apple/Google's in-app-purchase rules
 * forbid a tappable purchase-directed link there, so this is never rendered
 * as a Link/anchor on that surface — see Paywall.tsx.
 */
export const APP_DOMAIN = "snapdraw.app";

/**
 * Public store listing URLs for the landing page's App Store / Google Play
 * badges (backlog LAUNCH-26). Both start `undefined` — the badges only
 * render once their own URL is set, so staggered store approval (one
 * platform live before the other) never claims an app is downloadable
 * before it actually is. Set the real public listing URL here (never a
 * TestFlight/App Store Connect/Play Console/internal-testing link) once a
 * store review actually passes, verify it opens the public listing with no
 * login, then deploy.
 */
export const APP_STORE_URL: string | undefined = undefined;
export const GOOGLE_PLAY_URL: string | undefined = undefined;

/**
 * Per-user document storage quota, shown as a progress indicator in the app
 * shell (see getStorageStats in @/lib/documents). Documents are JSON text in
 * Postgres — a typical quote/G702 is a few KB, so 100MB is generously
 * thousands of documents per user. At Supabase's storage-overage pricing
 * (~$0.125/GB-month beyond the plan's included allotment), a fully-used
 * quota costs roughly $0.0125/user/month — not a real cost concern even if
 * every user maxes it out. Purely a sanity ceiling, not a monetization lever.
 */
export const STORAGE_QUOTA_BYTES = 100 * 1024 * 1024;

/**
 * Gates the "Load/Clear sample data" buttons on the Documents page (backlog
 * LAUNCH-4). Deliberately fail-closed — unset/anything-but-"true" disables
 * them — so a new environment that forgets to set this doesn't
 * accidentally ship the buttons to real customers. Per LAUNCH-18's planned
 * Test/Production split: set `VITE_ENABLE_SAMPLE_DATA=true` in Cloudflare
 * Pages' Test-environment env vars (needed there permanently, not just
 * during initial build-out), leave it unset in Production's.
 */
export const ENABLE_SAMPLE_DATA = import.meta.env.VITE_ENABLE_SAMPLE_DATA === "true";
